import { type Capability } from './permissions.js';
import { type TransactionStatus } from './numbering.js';

/**
 * Phase 2C - laundry processing workflow.
 *
 * The controlled STATUS flow stays untouched (PENDING -> WASHING ->
 * FOLDING -> READY -> RELEASED -> DONE). These stages describe the
 * operational work that happens INSIDE the WASHING and FOLDING statuses:
 *
 *   WASHING:  INITIAL_WASH -> FINAL_WASH -> DRYING -> (ADDITIONAL_DRYING)*
 *   FOLDING:  FOLDING
 *
 * Every completed stage snapshots the item accountability chain:
 * expected -> actual -> difference, responsible employee, machine,
 * branch and timestamps. Nothing here is ever rewritten.
 */
export const PROCESSING_STAGES = [
  'INITIAL_WASH',
  'FINAL_WASH',
  'DRYING',
  'ADDITIONAL_DRYING',
  'FOLDING',
] as const;
export type ProcessingStage = (typeof PROCESSING_STAGES)[number];

export const PROCESSING_STAGE_LABELS: Record<ProcessingStage, string> = {
  INITIAL_WASH: 'Initial / hand wash',
  FINAL_WASH: 'Final wash (machine)',
  DRYING: 'Drying',
  ADDITIONAL_DRYING: 'Additional drying',
  FOLDING: 'Folding',
};

/** Short labels for chips/steppers on the employee tablets. */
export const PROCESSING_STAGE_SHORT_LABELS: Record<ProcessingStage, string> = {
  INITIAL_WASH: 'Initial wash',
  FINAL_WASH: 'Final wash',
  DRYING: 'Dry',
  ADDITIONAL_DRYING: 'Add. drying',
  FOLDING: 'Folding',
};

/** The transaction status a stage may be recorded under (server-enforced). */
export const STAGE_STATUS_WINDOW: Record<ProcessingStage, TransactionStatus> = {
  INITIAL_WASH: 'WASHING',
  FINAL_WASH: 'WASHING',
  DRYING: 'WASHING',
  ADDITIONAL_DRYING: 'WASHING',
  FOLDING: 'FOLDING',
};

/** Resource identifiers used on the laundry floor (paper sheet: W1, W2 ...). */
export const WASHING_MACHINES = ['W1', 'W2', 'W3'] as const;
export const DRYING_MACHINES = ['D1', 'D2'] as const;

/** Machine pool per stage; empty = manual stage, no resource needed. */
export const STAGE_MACHINES: Record<ProcessingStage, readonly string[]> = {
  INITIAL_WASH: [],
  FINAL_WASH: WASHING_MACHINES,
  DRYING: DRYING_MACHINES,
  ADDITIONAL_DRYING: DRYING_MACHINES,
  FOLDING: [],
};

/** Capability required to record a stage (ADMIN holds both). */
export const STAGE_CAPABILITY: Record<ProcessingStage, Capability> = {
  INITIAL_WASH: 'washing.manage',
  FINAL_WASH: 'washing.manage',
  DRYING: 'washing.manage',
  ADDITIONAL_DRYING: 'washing.manage',
  FOLDING: 'folding.manage',
};

/**
 * Required wash pipeline in order. ADDITIONAL_DRYING is deliberately not
 * part of the sequence: the dryer can be re-run any number of times and
 * is recorded on the SAME transaction (never a new one).
 */
export const WASH_STAGE_SEQUENCE = ['INITIAL_WASH', 'FINAL_WASH', 'DRYING'] as const;

/** Capability needed to VIEW a processing area's queue. */
export const AREA_CAPABILITY = {
  washing: 'washing.view',
  folding: 'folding.view',
} as const;
export type ProcessingArea = keyof typeof AREA_CAPABILITY;

export function isProcessingStage(value: unknown): value is ProcessingStage {
  return typeof value === 'string' && (PROCESSING_STAGES as readonly string[]).includes(value);
}

/**
 * Next unfinished required wash stage, or null when drying is complete.
 * `completed` = stages with finished counts for the transaction.
 */
export function nextWashStage(completed: readonly ProcessingStage[]): ProcessingStage | null {
  for (const stage of WASH_STAGE_SEQUENCE) {
    if (!completed.includes(stage)) return stage;
  }
  return null;
}

/** True once the initial/final/dry pipeline is finished (handoff allowed). */
export function washingPipelineComplete(completed: readonly ProcessingStage[]): boolean {
  return nextWashStage(completed) === null;
}
